import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChildType, useItems } from '@techstyle/react-virtual-scroll';
import PaginatedGridBatch from './PaginatedGridBatch';
var PaginatedGrid = /*#__PURE__*/React.memo(function PaginatedGrid(_ref) {
  var as = _ref.as,
      _ref$currentPage = _ref.currentPage,
      currentPage = _ref$currentPage === void 0 ? 1 : _ref$currentPage,
      slots = _ref.slots,
      getItems = _ref.getItems,
      itemCount = _ref.itemCount,
      loadItems = _ref.loadItems,
      _ref$stepSize = _ref.stepSize,
      stepSize = _ref$stepSize === void 0 ? 1 : _ref$stepSize,
      maxBatchCount = _ref.maxBatchCount,
      renderItems = _ref.renderItems,
      targetBatchSize = _ref.targetBatchSize;

  var _useItems = useItems(itemCount, slots, stepSize, targetBatchSize, maxBatchCount, getItems, loadItems),
      batches = _useItems.batches,
      getItemsWithSlots = _useItems.getItemsWithSlots;

  var children = useMemo(() => {
    if (currentPage > 0) {
      // move batches to a single array, since we only want to show one at a time
      var mappedBatches = [];
      (batches || []).forEach(batch => {
        if (batch.childType === ChildType.BATCH) {
          mappedBatches.push(...batch.children);
        }

        if (batch.childType === ChildType.ITEM) {
          mappedBatches.push(batch);
        }
      });
      var batch = mappedBatches[currentPage - 1];

      if (batch) {
        return /*#__PURE__*/React.createElement(PaginatedGridBatch, {
          as: as,
          childrenArray: batch.children,
          childType: batch.childType,
          getItems: getItemsWithSlots,
          loadItems: loadItems,
          renderItems: renderItems,
          slots: batch.slots,
          startIndex: batch.itemStartIndex,
          stopIndex: batch.itemStopIndex
        });
      }
    }

    return null;
  }, [as, batches, currentPage, getItemsWithSlots, loadItems, renderItems]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
});
PaginatedGrid.propTypes = {
  /**
   * The element type that individual batches will render, defaults to `div`.
   * When rendering a `<table>`, you'll want this to be `tbody`.
   */
  as: PropTypes.elementType,

  /**
   * The number of the page to be rendered. Defaults to 1.
   */
  currentPage: PropTypes.number,

  /**
   * Function to synchronously return the range of items from `startIndex` to
   * `stopIndex`, or nothing if they're not all available. If not available then
   * `loadItems` will be called with the same range.
   */
  getItems: PropTypes.func,

  /**
   * The total number of items in the full list (even if they're not all loaded
   * yet). If not passed, then an initial `loadItems` call will be made, which
   * should result in the correct value being passed.
   */
  itemCount: PropTypes.number,

  /**
   * Function to trigger loading of the range of items from `startIndex` to
   * `stopIndex`. When the items load it should ultimately cause `getItems` to
   * update.
   */
  loadItems: PropTypes.func,

  /**
   * The maximum number of batches to render before recursively batching the
   * batches. This may be necessary if there are a lot of items, but is not
   * always possible (for example in a `<table>`, where `<tbody>` cannot be
   * nested). Set to `null` to disable recursive batching.
   */
  maxBatchCount: PropTypes.number,

  /**
   * Function to render the given `items` or a placeholder for them if not
   * available. It is called with `items`, `isVisible`, `startIndex`,
   * `stopIndex`, and `lastKnownHeight`.
   */
  renderItems: PropTypes.func.isRequired,

  /** Adds inserts into the given items inside the PaginatedGrid list. This is
   * subject to grouping and layouting so that the number of `stepSize` is
   * never broken up for a group, depending on the insert's size and position.
   */
  slots: PropTypes.arrayOf(PropTypes.shape({
    cellsTall: PropTypes.number,
    cellsWide: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['full'])]),
    column: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['right', 'left'])]),
    maxColumns: PropTypes.number,
    minColumns: PropTypes.number,
    row: PropTypes.number
  })),

  /**
   * Specifies the minimum size of a group of `slots`, e.g. number of columns
   * in a grid. This gives us an indication of how far along we should
   * move when changing the size of a batch.
   */
  stepSize: PropTypes.number,

  /**
   * The target number of items to render in each batch. It's possible that
   * during batching the size will be smaller (if there aren't enough items to
   * fill the batch) or larger (if a batch must be extended to contain a
   * multiple of some number of items, for grids).
   */
  targetBatchSize: PropTypes.number
};
export default PaginatedGrid;